/**
 * sound-engine.ts - Motor de Áudio de Bancada via Web Audio API
 * Baseado na Seção 13 do Guia Profissional de Animações Realistas
 * Filosofia Fábio Akita: Fail-Visible, Zero Dependências Pesadas e Fallback Sintético Resiliente.
 */

import type { AudioSampleId, SoundEngineOptions } from "./motion-types.js";

export class Adv150SoundEngine {
  private audioCtx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private isMuted: boolean;
  private volume: number;
  private readonly isSupported: boolean;
  private motorOscillator: OscillatorNode | null = null;
  private motorGain: GainNode | null = null;
  private lastTriggeredClickTime = 0;

  constructor(options: SoundEngineOptions = {}) {
    this.volume = options.masterVolume ?? 0.8;
    this.isMuted = options.muted ?? false;

    // Detecção segura de suporte sem falhas em Node / Vitest
    const hasAudioContext =
      typeof window !== "undefined" && ("AudioContext" in window || "webkitAudioContext" in window);

    this.isSupported = hasAudioContext;
  }

  /**
   * Inicializa o AudioContext no primeiro gesto do usuário (requisito de autoplay)
   */
  public ensureContext(): boolean {
    if (!this.isSupported) return false;

    if (!this.audioCtx && typeof window !== "undefined") {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

      if (AudioContextClass) {
        this.audioCtx = new AudioContextClass();
        this.masterGain = this.audioCtx.createGain();
        this.masterGain.gain.value = this.isMuted ? 0 : this.volume;
        this.masterGain.connect(this.audioCtx.destination);
      }
    }

    if (this.audioCtx && this.audioCtx.state === "suspended") {
      void this.audioCtx.resume();
    }

    return this.audioCtx !== null;
  }

  public getVolume(): number {
    return this.volume;
  }

  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.masterGain && this.audioCtx && !this.isMuted) {
      this.masterGain.gain.setValueAtTime(this.volume, this.audioCtx.currentTime);
    }
  }

  public isMute(): boolean {
    return this.isMuted;
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    if (this.masterGain && this.audioCtx) {
      this.masterGain.gain.setValueAtTime(
        this.isMuted ? 0 : this.volume,
        this.audioCtx.currentTime,
      );
    }
    return this.isMuted;
  }

  /**
   * Dispara o clique metálico do torquímetro ISO 6789 a 59 N.m.
   * Emprega síntese física com dois harmônicos de impacto e decaimento exponencial curto (65 ms).
   */
  public playTorqueClick(): boolean {
    if (this.isMuted || !this.ensureContext() || !this.audioCtx || !this.masterGain) {
      return false;
    }

    const now = this.audioCtx.currentTime;
    // Debounce de segurança contra múltiplos cliques no mesmo frame (mínimo 150 ms)
    if (now - this.lastTriggeredClickTime < 0.15) {
      return false;
    }
    this.lastTriggeredClickTime = now;

    try {
      // 1. Harmônico metálico principal (aço cromo-vanádio: ~1.180 Hz)
      const oscPrimary = this.audioCtx.createOscillator();
      const gainPrimary = this.audioCtx.createGain();

      oscPrimary.type = "sine";
      oscPrimary.frequency.setValueAtTime(1180, now);
      // Queda sutil de pitch no impacto
      oscPrimary.frequency.exponentialRampToValueAtTime(750, now + 0.05);

      gainPrimary.gain.setValueAtTime(0.9, now);
      gainPrimary.gain.exponentialRampToValueAtTime(0.001, now + 0.065);

      oscPrimary.connect(gainPrimary);
      gainPrimary.connect(this.masterGain);

      oscPrimary.start(now);
      oscPrimary.stop(now + 0.07);

      // 2. Harmônico secundário agudo do mecanismo de engate (2.740 Hz)
      const oscSnap = this.audioCtx.createOscillator();
      const gainSnap = this.audioCtx.createGain();

      oscSnap.type = "triangle";
      oscSnap.frequency.setValueAtTime(2740, now);
      gainSnap.gain.setValueAtTime(0.6, now);
      gainSnap.gain.exponentialRampToValueAtTime(0.001, now + 0.035);

      oscSnap.connect(gainSnap);
      gainSnap.connect(this.masterGain);

      oscSnap.start(now);
      oscSnap.stop(now + 0.04);

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Inicia ou atualiza a rotação audível do motor eSP 4T (frequência proporcional ao RPM)
   * RPM de marcha lenta: 1.700 RPM -> f0 = 14,1 Hz
   * RPM de pico: 8.500 RPM -> f0 = 70,8 Hz
   */
  public updateMotorAudio(rpm: number, isRunning: boolean): void {
    if (!this.ensureContext() || !this.audioCtx || !this.masterGain) return;

    const now = this.audioCtx.currentTime;

    if (!isRunning || this.isMuted || rpm <= 0) {
      if (this.motorGain) {
        this.motorGain.gain.setValueAtTime(0, now);
      }
      return;
    }

    const fundamentalFreq = Math.max(12, Math.min(80, rpm / 120));

    if (!this.motorOscillator) {
      this.motorOscillator = this.audioCtx.createOscillator();
      this.motorGain = this.audioCtx.createGain();

      this.motorOscillator.type = "sawtooth";
      this.motorOscillator.frequency.setValueAtTime(fundamentalFreq, now);

      this.motorGain.gain.setValueAtTime(0.12, now);

      this.motorOscillator.connect(this.motorGain);
      this.motorGain.connect(this.masterGain);

      this.motorOscillator.start(now);
    } else if (this.motorGain) {
      this.motorOscillator.frequency.setTargetAtTime(fundamentalFreq, now, 0.05);
      this.motorGain.gain.setTargetAtTime(0.12, now, 0.05);
    }
  }

  public stopAll(): void {
    if (this.motorGain && this.audioCtx) {
      this.motorGain.gain.setValueAtTime(0, this.audioCtx.currentTime);
    }
  }

  public dispose(): void {
    this.stopAll();
    if (this.motorOscillator) {
      try {
        this.motorOscillator.stop();
        this.motorOscillator.disconnect();
      } catch {
        // Ignorar se já finalizado
      }
      this.motorOscillator = null;
    }
    if (this.audioCtx && this.audioCtx.state !== "closed") {
      void this.audioCtx.close();
      this.audioCtx = null;
    }
  }
}
